import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Crown, Medal, Trophy, Eye, EyeOff, RefreshCw, Loader2 } from 'lucide-react';
import { useSyncRefresh } from '../hooks/useSyncRefresh';

import API from '../utils/api';

var SUBSCRIPTION_COLORS = {
  free: { bg: 'rgba(113,113,122,0.1)', text: '#71717A', border: 'rgba(113,113,122,0.2)' },
  basic: { bg: 'rgba(59,130,246,0.1)', text: '#3B82F6', border: 'rgba(59,130,246,0.2)' },
  standard: { bg: 'rgba(139,92,246,0.1)', text: '#5A4E9E', border: 'rgba(139,92,246,0.2)' },
  pro: { bg: 'rgba(249,115,22,0.1)', text: '#F97316', border: 'rgba(249,115,22,0.2)' },
};

function LeaderboardRow({ entry, isFirst }) {
  var sc = SUBSCRIPTION_COLORS[entry.subscription] || SUBSCRIPTION_COLORS.free;
  var signupDate = entry.signup_date ? new Date(entry.signup_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';

  return (
    <div className={'flex items-center gap-4 p-3 sm:p-4 rounded-xl border transition-all ' + (isFirst ? 'border-[#F59E0B]/30 bg-[#F59E0B]/[0.03]' : 'border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03]')}
      data-testid={'leaderboard-row-' + entry.rank}>
      {/* Rank */}
      <div className="w-8 sm:w-10 text-center flex-shrink-0">
        {entry.rank === 1 ? (
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center">
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        ) : entry.rank === 2 ? (
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#94A3B8] to-[#64748B] flex items-center justify-center">
            <Medal className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        ) : entry.rank === 3 ? (
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#D97706] to-[#92400E] flex items-center justify-center">
            <Medal className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        ) : (
          <span className="text-sm sm:text-base font-bold text-white/40">#{entry.rank}</span>
        )}
      </div>

      {/* Username */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {isFirst && <Crown className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />}
          <span className={'text-sm sm:text-base font-semibold truncate ' + (isFirst ? 'text-[#F59E0B]' : 'text-white')}
            style={isFirst ? { textShadow: '0 0 12px rgba(245,158,11,0.5), 0 0 24px rgba(245,158,11,0.2)' } : {}}>
            {entry.username}
          </span>
        </div>
        <p className="text-xs sm:text-xs text-white/30">Member since {signupDate}</p>
      </div>

      {/* Subscription */}
      <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full text-xs sm:text-xs font-bold uppercase tracking-wider flex-shrink-0"
        style={{ background: sc.bg, color: sc.text, border: '1px solid ' + sc.border }}>
        {entry.subscription}
      </span>

      {/* Points */}
      <div className="text-right flex-shrink-0">
        <p className={'text-base sm:text-lg font-bold ' + (isFirst ? 'text-[#F59E0B]' : 'text-white')}>
          {entry.points.toLocaleString()}
        </p>
        <p className="text-xs text-white/30">pts</p>
      </div>
    </div>
  );
}

function Leaderboard() {
  var { user } = useAuth();
  var [leaderboard, setLeaderboard] = useState([]);
  var [myOptIn, setMyOptIn] = useState(true);
  var [loading, setLoading] = useState(true);
  var [toggling, setToggling] = useState(false);

  var loadLeaderboard = useCallback(function () {
    setLoading(true);
    axios.get(API + '/compliance/leaderboard', { withCredentials: true })
      .then(function (r) {
        setLeaderboard(r.data.leaderboard || []);
        setMyOptIn(r.data.my_opt_in !== false);
      })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }, []);

  useEffect(function () { loadLeaderboard(); }, [loadLeaderboard]);
  useSyncRefresh(loadLeaderboard);

  var handleToggle = function () {
    setToggling(true);
    axios.patch(API + '/compliance/leaderboard-toggle', { opt_in: !myOptIn }, { withCredentials: true })
      .then(function () {
        setMyOptIn(!myOptIn);
        loadLeaderboard();
      })
      .finally(function () { setToggling(false); });
  };

  return (
    <div className="space-y-4" data-testid="leaderboard">
      <div className="card-surface p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#F59E0B]/15 to-[#D97706]/15 border border-[#F59E0B]/15">
              <Trophy className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Leaderboard</h3>
              <p className="text-xs text-white/35">Top co-parents by points</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleToggle} disabled={toggling}
              className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ' +
                (myOptIn
                  ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20'
                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10')}
              data-testid="leaderboard-toggle">
              {myOptIn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {myOptIn ? 'Visible' : 'Hidden'}
            </button>
            <button onClick={loadLeaderboard} disabled={loading}
              className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/5 transition-all">
              <RefreshCw className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            </button>
          </div>
        </div>

        {!myOptIn && (
          <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs text-white/40">
            Your profile is hidden from the leaderboard. Toggle visibility to participate.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="py-12 text-center">
            <Trophy className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/40 text-sm">No participants yet</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-list">
            {leaderboard.map(function (entry) {
              return <LeaderboardRow key={entry.rank} entry={entry} isFirst={entry.rank === 1} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
